import { useState, useMemo, useEffect } from 'react';
import { useTemplateManagement } from './hooks/useTemplateManagement';
import { useInstrumentConfig } from './hooks/useInstrumentConfig';
import {
  useConfiguration as useConfigStore,
  createDefaultTemplate,
} from '../../../store/configurationStore';
import { getSectionsForInstrumentType } from './configs/sectionConfigs';
import { UIInstrumentType } from './types';
import Actions from '../../../store/Actions';
import useConfigurationStore from '../../../store/configurationStore';

export const useConfiguration = (
  initialTemplateId?: string | null,
  isVisible?: boolean
) => {
  const [isAddTemplateDialogOpen, setIsAddTemplateDialogOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set()
  );
  const [isExpandAllMode, setIsExpandAllMode] = useState(false);

  // Get availableHeaders from store and filter out hidden columns (starting with '_')
  const { availableHeaders: rawHeaders } = useConfigStore();
  const availableHeaders = useMemo(
    () => rawHeaders.filter(header => !header.startsWith('_')),
    [rawHeaders]
  );

  // Template management
  const { templates, activeTemplate, selectTemplate, createTemplate } =
    useTemplateManagement();

  // Instrument configuration
  const { activeInstrumentType, selectInstrumentType } = useInstrumentConfig();

  // Get visible sections for current instrument type
  const visibleSections = useMemo(() => {
    if (!activeInstrumentType) return [];
    return getSectionsForInstrumentType(activeInstrumentType);
  }, [activeInstrumentType]);

  // Load templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const loadedTemplates = await Actions.listConfigurationTemplates();
        const { setTemplates, setActiveTemplate } =
          useConfigurationStore.getState();

        if (loadedTemplates && loadedTemplates.length > 0) {
          // Have templates in DB - replace store with loaded templates
          setTemplates(loadedTemplates);

          // Sync to initial template if provided and exists
          if (initialTemplateId) {
            const templateExists = loadedTemplates.some(
              t => t.id === initialTemplateId
            );
            if (templateExists) {
              setActiveTemplate(initialTemplateId);
            } else {
              console.error(
                `[CONFIGURATION] Template ID "${initialTemplateId}" not found in loaded templates`
              );
            }
          }
        } else {
          // No templates in DB - create default Vault template
          const vaultTemplate = createDefaultTemplate('Vault');
          setTemplates([vaultTemplate]);
        }
      } catch (error) {
        console.error('Failed to load templates:', error);
        // On error, create default Vault template
        const vaultTemplate = createDefaultTemplate('Vault');
        const { setTemplates } = useConfigurationStore.getState();
        setTemplates([vaultTemplate]);
      }
    };

    loadTemplates();
  }, [initialTemplateId]); // Re-run when initialTemplateId changes

  // Sync to initial template when modal becomes visible
  useEffect(() => {
    if (isVisible && initialTemplateId) {
      const { templates, setActiveTemplate } = useConfigurationStore.getState();
      const templateExists = templates.some(t => t.id === initialTemplateId);
      if (templateExists) {
        setActiveTemplate(initialTemplateId);
      } else {
        console.error(
          `[CONFIGURATION] Template ID "${initialTemplateId}" not found in templates`
        );
      }
    }
  }, [isVisible, initialTemplateId]);

  // Auto-expand all sections when switching tabs if in "expand all mode"
  useEffect(() => {
    if (isExpandAllMode && visibleSections.length > 0) {
      // If expand all mode is active, expand all sections in the new tab
      const allSectionIds = visibleSections.map(section => section.id);
      setExpandedSections(new Set(allSectionIds));
    } else if (!isExpandAllMode) {
      // If not in expand all mode, reset to collapsed
      setExpandedSections(new Set());
    }
  }, [activeInstrumentType, visibleSections, isExpandAllMode]);

  // Handler for template selection
  const handleTemplateSelect = (templateId: string): void => {
    selectTemplate(templateId);
  };

  // Handler for opening add template dialog
  const handleAddTemplate = (): void => {
    setIsAddTemplateDialogOpen(true);
  };

  // Handler for closing add template dialog
  const handleCloseAddTemplateDialog = (): void => {
    setIsAddTemplateDialogOpen(false);
  };

  // Handler for creating new template
  const handleCreateTemplate = (templateName: string): void => {
    createTemplate(templateName);
    setIsAddTemplateDialogOpen(false);
  };

  // Handler for instrument type change
  const handleInstrumentTypeChange = (
    instrumentType: UIInstrumentType
  ): void => {
    selectInstrumentType(instrumentType);
  };

  // Handler for accordion expansion
  const handleAccordionChange = (section: string): void => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
    // Manual expansion/collapse exits "expand all mode"
    setIsExpandAllMode(false);
  };

  // Handler for expanding all sections
  const handleExpandAll = (): void => {
    const allSectionIds = visibleSections.map(section => section.id);
    setExpandedSections(new Set(allSectionIds));
    setIsExpandAllMode(true);
  };

  // Handler for collapsing all sections
  const handleCollapseAll = (): void => {
    setExpandedSections(new Set());
    setIsExpandAllMode(false);
  };

  return {
    // Data
    templates,
    activeTemplateId: activeTemplate?.id || null,
    activeInstrumentType,
    availableHeaders,
    visibleSections,

    // Handlers
    handleTemplateSelect,
    handleAddTemplate,
    handleInstrumentTypeChange,

    // Dialog state
    isAddTemplateDialogOpen,
    handleCloseAddTemplateDialog,
    handleCreateTemplate,

    // Accordion state
    expandedSections,
    handleAccordionChange,
    handleExpandAll,
    handleCollapseAll,
  };
};
